import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/session';
import Icon from '@/components/Icon';

// ============================================================
// Stocora — Settings › Practice Profile (Block A, 2026-07-14)
// ============================================================
// Drew Update #4 §3. The keystone settings surface: the clinic's
// purchasing contact (email / phone / hours) plus the two audit
// thresholds (dead-stock, expiring), both stored on
// clinic_settings (migration 20260714120000).
//
// Why it matters beyond "a settings tab":
//   - purchasing_email is the reply-to the vendor PO needs
//     (Block B) — without it the PO can't front a clinic address.
//   - dead_stock_days / expiring_days replace the hardcoded
//     detector windows once Block D wires the scan to read them.
//     Until Block D lands, editing thresholds here writes the
//     row but does not yet change scan output — that wiring is a
//     separate, deliberately-decoupled ticket.
//
// Thresholds: default 90 days, adjustable in 30-day increments
// (Drew §3). Enforced both in the DB (check constraint) and in
// the UI (stepper moves by 30, bounded 30..720).
//
// Permissions: writes route through the update_clinic_settings()
// RPC, which enforces has_permission('clinic.settings.edit')
// (owner/manager) server-side. The page mirrors that gate off the
// effective role (viewAsRole ?? real role) so staff see a
// read-only view rather than a silent RPC denial on save.
//
// Data pattern mirrors VendorsSettingsPage / AuditPage: load
// inside an effect with a cancelled flag; a refetchToken bump
// re-runs it after a save.
// ============================================================

interface SettingsRow {
  readonly purchasing_name: string | null;
  readonly purchasing_email: string | null;
  readonly purchasing_phone: string | null;
  readonly purchasing_hours: string | null;
  readonly dead_stock_days: number;
  readonly expiring_days: number;
}

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; settings: SettingsRow };

const DEFAULTS: SettingsRow = {
  purchasing_name: null,
  purchasing_email: null,
  purchasing_phone: null,
  purchasing_hours: null,
  dead_stock_days: 90,
  expiring_days: 90,
};

const THRESHOLD_MIN = 30;
const THRESHOLD_MAX = 720;
const THRESHOLD_STEP = 30;

// Same forgiving email check used on the Vendors page: catch
// obvious typos, allow empty (= clear the field).
function isPlausibleEmail(value: string): boolean {
  const v = value.trim();
  if (v === '') return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function PracticeProfilePage() {
  const session = useSession();
  const clinicId = session.activeMembership.clinic_id;
  const effectiveRole = session.viewAsRole ?? session.activeMembership.role;
  const canEdit = effectiveRole === 'owner' || effectiveRole === 'manager';

  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const [refetchToken, setRefetchToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    (async () => {
      const { data, error } = await supabase
        .from('clinic_settings')
        .select(
          'purchasing_name, purchasing_email, purchasing_phone, purchasing_hours, dead_stock_days, expiring_days',
        )
        .eq('clinic_id', clinicId)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        setState({ kind: 'error', message: error.message });
        return;
      }

      // No row yet = clinic hasn't set anything → show defaults.
      setState({ kind: 'loaded', settings: data ?? DEFAULTS });
    })();

    return () => {
      cancelled = true;
    };
  }, [clinicId, refetchToken]);

  const handleSaved = useCallback(() => {
    setRefetchToken((t) => t + 1);
  }, []);

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="text-slate-400 hover:text-slate-700 transition"
          aria-label="Back to settings"
        >
          <Icon name="chevright" className="w-5 h-5 rotate-180" strokeWidth={2} />
        </button>
        <h1 className="text-base font-semibold text-slate-900">Practice Profile</h1>
      </div>

      <p className="text-[12px] text-slate-500 leading-snug">
        Your clinic&apos;s purchasing contact appears on vendor orders as the
        reply-to. Thresholds control when items are flagged as dead stock or
        expiring.
        {!canEdit && (
          <span className="block mt-1 text-slate-400">
            You have view-only access. A manager can make changes.
          </span>
        )}
      </p>

      {state.kind === 'loading' && (
        <div className="py-10 flex justify-center" aria-label="Loading settings">
          <div className="w-6 h-6 border-[3px] border-slate-200 border-t-action rounded-full animate-spin" />
        </div>
      )}

      {state.kind === 'error' && (
        <div className="rounded-lg bg-rose-50 border border-rose-100 p-3 text-[13px] text-rose-800">
          Couldn&apos;t load settings: {state.message}
        </div>
      )}

      {state.kind === 'loaded' && (
        <ProfileForm
          clinicId={clinicId}
          initial={state.settings}
          canEdit={canEdit}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string };

function ProfileForm({
  clinicId,
  initial,
  canEdit,
  onSaved,
}: {
  clinicId: string;
  initial: SettingsRow;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [email, setEmail] = useState(initial.purchasing_email ?? '');
  const [phone, setPhone] = useState(initial.purchasing_phone ?? '');
  const [hours, setHours] = useState(initial.purchasing_hours ?? '');
  const [name, setName] = useState(initial.purchasing_name ?? '');
  const [deadDays, setDeadDays] = useState(initial.dead_stock_days);
  const [expDays, setExpDays] = useState(initial.expiring_days);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });

  const emailValid = isPlausibleEmail(email);
  const dirty =
    email.trim() !== (initial.purchasing_email ?? '') ||
    phone.trim() !== (initial.purchasing_phone ?? '') ||
    hours.trim() !== (initial.purchasing_hours ?? '') ||
    name.trim() !== (initial.purchasing_name ?? '') ||
    deadDays !== initial.dead_stock_days ||
    expDays !== initial.expiring_days;

  const canSave = canEdit && dirty && emailValid && save.kind !== 'saving';

  const clamp = (n: number) =>
    Math.min(THRESHOLD_MAX, Math.max(THRESHOLD_MIN, n));

  const handleSave = useCallback(async () => {
    setSave({ kind: 'saving' });

    // RPC contract (three-state, per the Update #5 §1 fix):
    //   null  -> leave unchanged   (partial PATCH; not used from here)
    //   ''    -> clear the field   (stored as NULL)
    //   value -> set it
    // This form submits every field on each save, so "leave unchanged"
    // never applies: a blank input genuinely means the operator cleared
    // it. Sending the trimmed value (''-when-blank) is therefore both
    // correct and complete. The RPC trims + nullifs server-side too, so
    // this is belt-and-braces rather than the only guard.
    const { error } = await supabase.rpc('update_clinic_settings', {
      p_clinic_id: clinicId,
      p_purchasing_email: email.trim(),
      p_purchasing_phone: phone.trim(),
      p_purchasing_hours: hours.trim(),
      p_purchasing_name: name.trim(),
      p_dead_stock_days: deadDays,
      p_expiring_days: expDays,
    });

    if (error) {
      console.error('[stocora] Practice Profile save failed:', error);
      // Name the actual problem where we can. Drew (Update #5 §1) hit a
      // bare "Couldn't save — try again" on a constraint violation with
      // no clue which field was at fault. The blank-field cause is fixed
      // in 20260714150000, but a genuinely invalid value (e.g. a 2-char
      // phone) can still trip a CHECK, so map those to something useful.
      const msg = (() => {
        if (error.message.includes('permission')) {
          return "You don't have permission to change these settings";
        }
        if (error.code === '23514') {
          if (error.message.includes('purchasing_phone')) {
            return 'That phone number doesn\u2019t look right — use 3–40 characters, or leave it blank';
          }
          if (error.message.includes('purchasing_email')) {
            return 'That email doesn\u2019t look right — check it, or leave it blank';
          }
          if (error.message.includes('purchasing_name')) {
            return 'That name is too long \u2014 keep it under 80 characters';
          }
          if (error.message.includes('purchasing_hours')) {
            return 'Hours is too long — keep it under 200 characters';
          }
          if (
            error.message.includes('dead_stock_days') ||
            error.message.includes('expiring_days')
          ) {
            return 'Thresholds must be 30–720 days, in 30-day steps';
          }
          return 'One of those values isn\u2019t valid — check and try again';
        }
        return "Couldn't save — try again";
      })();
      setSave({ kind: 'error', message: msg });
      return;
    }

    setSave({ kind: 'saved' });
    onSaved();
  }, [clinicId, email, phone, hours, name, deadDays, expDays, onSaved]);

  return (
    <div className="space-y-4">
      {/* Purchasing contact */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <h2 className="text-[13px] font-semibold text-slate-900">
          Purchasing contact
        </h2>

        <Field
          label="Contact name"
          hint="Optional — signs buyback emails to reps"
        >
          <input
            type="text"
            value={name}
            disabled={!canEdit}
            onChange={(e) => {
              setName(e.target.value);
              if (save.kind !== 'idle') setSave({ kind: 'idle' });
            }}
            placeholder="Sarah Chen"
            className={inputCls(true)}
          />
        </Field>

        <Field label="Purchasing email" hint="Vendors reply here on orders">
          <input
            type="email"
            inputMode="email"
            value={email}
            disabled={!canEdit}
            onChange={(e) => {
              setEmail(e.target.value);
              if (save.kind !== 'idle') setSave({ kind: 'idle' });
            }}
            placeholder="purchasing@yourclinic.com"
            className={inputCls(email === '' || emailValid)}
          />
          {!emailValid && email !== '' && (
            <span className="text-[11px] text-rose-600 mt-1 block">
              That doesn&apos;t look like a valid email
            </span>
          )}
        </Field>

        <Field label="Phone" hint="Optional — shown on orders if set">
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            disabled={!canEdit}
            onChange={(e) => {
              setPhone(e.target.value);
              if (save.kind !== 'idle') setSave({ kind: 'idle' });
            }}
            placeholder="(555) 123-4567"
            className={inputCls(true)}
          />
        </Field>

        <Field label="Hours" hint="Optional — e.g. Mon–Fri 8am–5pm">
          <input
            type="text"
            value={hours}
            disabled={!canEdit}
            onChange={(e) => {
              setHours(e.target.value);
              if (save.kind !== 'idle') setSave({ kind: 'idle' });
            }}
            placeholder="Mon–Fri 8am–5pm"
            className={inputCls(true)}
          />
        </Field>
      </section>

      {/* Audit thresholds */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <h2 className="text-[13px] font-semibold text-slate-900">
          Audit thresholds
        </h2>
        <p className="text-[12px] text-slate-500 leading-snug -mt-1">
          Adjustable in 30-day steps.
        </p>

        <ThresholdStepper
          label="Dead stock after"
          value={deadDays}
          disabled={!canEdit}
          onChange={(n) => {
            setDeadDays(clamp(n));
            if (save.kind !== 'idle') setSave({ kind: 'idle' });
          }}
        />
        <ThresholdStepper
          label="Flag expiring within"
          value={expDays}
          disabled={!canEdit}
          onChange={(n) => {
            setExpDays(clamp(n));
            if (save.kind !== 'idle') setSave({ kind: 'idle' });
          }}
        />
      </section>

      {/* Save bar */}
      {canEdit && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            className="h-10 px-5 rounded-lg bg-action hover:bg-action-hover active:bg-action-active disabled:bg-slate-200 disabled:text-slate-400 text-white text-[13px] font-semibold transition"
          >
            {save.kind === 'saving' ? 'Saving…' : 'Save changes'}
          </button>
          {save.kind === 'saved' && (
            <span className="text-[11px] text-success inline-flex items-center gap-1">
              <Icon name="check" className="w-3.5 h-3.5" strokeWidth={2.5} />
              Saved
            </span>
          )}
          {save.kind === 'error' && (
            <span className="text-[11px] text-rose-600">{save.message}</span>
          )}
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[12px] font-medium text-slate-700">{label}</span>
      {hint && <span className="text-[11px] text-slate-400 ml-1.5">{hint}</span>}
      {children}
    </label>
  );
}

function ThresholdStepper({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[12px] font-medium text-slate-700">{label}</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || value <= THRESHOLD_MIN}
          onClick={() => onChange(value - THRESHOLD_STEP)}
          aria-label={`Decrease ${label}`}
          className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 disabled:text-slate-300 hover:bg-slate-50 transition flex items-center justify-center"
        >
          <Icon name="minus" className="w-4 h-4" strokeWidth={2.5} />
        </button>
        <span className="w-[68px] text-center text-[13px] font-semibold text-slate-900 tabular-nums">
          {value} days
        </span>
        <button
          type="button"
          disabled={disabled || value >= THRESHOLD_MAX}
          onClick={() => onChange(value + THRESHOLD_STEP)}
          aria-label={`Increase ${label}`}
          className="w-8 h-8 rounded-lg border border-slate-200 text-slate-600 disabled:text-slate-300 hover:bg-slate-50 transition flex items-center justify-center"
        >
          <Icon name="plus" className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

function inputCls(valid: boolean): string {
  return `mt-1 w-full h-10 px-3 rounded-lg border text-[13px] transition outline-none disabled:bg-slate-50 disabled:text-slate-400 ${
    valid
      ? 'border-slate-200 focus:border-action'
      : 'border-rose-300 focus:border-rose-400'
  }`;
}
